// src/components/Finance/FinancialSummaryCards.jsx
import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  AccountBalanceWallet,
  TrendingUp,
  Percent,
  LocalAtm,
  Pending,
  CheckCircle,
} from '@mui/icons-material';
import { formatCurrency } from '../../services/financeService';

export const FinancialSummaryCards = ({ stats, loading = false, variant = 'vendor' }) => {
  if (loading) {
    return (
      <Grid container spacing={2}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Grid item xs={6} sm={2.4} key={i}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Box sx={{ height: 60, bgcolor: 'grey.200', borderRadius: 1 }} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  const {
    availableBalance = 0,
    totalRevenue = 0,
    totalCommission = 0,
    totalPayouts = 0,
    pendingPayouts = 0,
    processingPayouts = 0,
  } = stats;

  const isAdmin = variant === 'admin';

  return (
    <Grid container spacing={2}>
      <Grid item xs={6} sm={2.4}>
        <Card sx={{ borderRadius: 2, height: '100%' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccountBalanceWallet color="success" fontSize="small" />
              <Typography variant="caption" color="text.secondary">
                Available
              </Typography>
            </Stack>
            <Typography variant="h6" fontWeight="bold" color="success.main">
              {formatCurrency(availableBalance)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={2.4}>
        <Card sx={{ borderRadius: 2, height: '100%' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TrendingUp color="primary" fontSize="small" />
              <Typography variant="caption" color="text.secondary">
                Revenue
              </Typography>
            </Stack>
            <Typography variant="h6" fontWeight="bold" color="primary.main">
              {formatCurrency(totalRevenue)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={2.4}>
        <Card sx={{ borderRadius: 2, height: '100%' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Percent color="secondary" fontSize="small" />
              <Typography variant="caption" color="text.secondary">
                Commission
              </Typography>
            </Stack>
            <Typography variant="h6" fontWeight="bold" color="secondary.main">
              {formatCurrency(totalCommission)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={2.4}>
        <Card sx={{ borderRadius: 2, height: '100%' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LocalAtm color="info" fontSize="small" />
              <Typography variant="caption" color="text.secondary">
                Payouts
              </Typography>
            </Stack>
            <Typography variant="h6" fontWeight="bold" color="info.main">
              {formatCurrency(totalPayouts)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={6} sm={2.4}>
        <Card sx={{ borderRadius: 2, height: '100%' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Pending color="warning" fontSize="small" />
              <Typography variant="caption" color="text.secondary">
                Pending
              </Typography>
            </Stack>
            <Typography variant="h6" fontWeight="bold" color="warning.main">
              {formatCurrency(pendingPayouts + processingPayouts)}
            </Typography>
            {processingPayouts > 0 && (
              <Chip 
                size="small" 
                label={`${formatCurrency(processingPayouts)} processing`}
                color="info"
                variant="outlined"
                sx={{ mt: 0.5, fontSize: '0.6rem' }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export const RevenueBreakdownBar = ({ stats }) => {
  const {
    totalRevenue = 0,
    totalCommission = 0,
  } = stats;

  const netEarnings = Math.max(0, totalRevenue - totalCommission);
  const commissionPercentage = totalRevenue > 0 ? (totalCommission / totalRevenue) * 100 : 0;
  const netPercentage = totalRevenue > 0 ? (netEarnings / totalRevenue) * 100 : 0;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Revenue Breakdown
      </Typography>
      <Stack spacing={1}>
        <Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption">Total Revenue</Typography>
            <Typography variant="caption" fontWeight="bold">
              {formatCurrency(totalRevenue)}
            </Typography>
          </Stack>
          <LinearProgress 
            variant="determinate" 
            value={100} 
            sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200' }}
          />
        </Box>
        <Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="secondary.main">Commission</Typography>
            <Typography variant="caption" fontWeight="bold" color="secondary.main">
              {formatCurrency(totalCommission)} ({commissionPercentage.toFixed(1)}%)
            </Typography>
          </Stack>
          <LinearProgress 
            variant="determinate" 
            value={commissionPercentage} 
            sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200' }}
            color="secondary"
          />
        </Box>
        <Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="success.main">Net Earnings</Typography>
            <Typography variant="caption" fontWeight="bold" color="success.main">
              {formatCurrency(netEarnings)} ({netPercentage.toFixed(1)}%)
            </Typography>
          </Stack>
          <LinearProgress 
            variant="determinate" 
            value={netPercentage} 
            sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200' }}
            color="success"
          />
        </Box>
      </Stack>
    </Box>
  );
};